import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import LogoSvg from "../../common/assets/images/logo1.svg";
import {
  EnvelopeIcon,
  PhoneIcon,
  LockClosedIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { register } from "../../services/AuthService";
import { toast } from "react-toastify";
import provincesData from "./province.json";

const Input = ({
  placeholder,
  type = "text",
  rightIcon,
  name,
  value,
  onChange,
  disabled,
}) => (
  <div className="relative">
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full h-11 md:h-12 rounded-[15px] border border-[#e5a7b7] bg-white/70 pl-4 pr-12 text-[#4b4b4b] placeholder-[#4b4b4b] focus:outline-none focus:ring-2 focus:ring-[#db3b6d]"
      disabled={disabled}
    />
    {rightIcon}
  </div>
);

const RegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: "",
    yearOfBirth: "",
    phoneNumber: "",
    email: "",
    school: "",
    city: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [provinces, setProvinces] = useState(provincesData);
  const [provincesLoading, setProvincesLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const isValidEmail = (email) =>
    /^[a-zA-Z](?!.*\.\.)[a-zA-Z0-9._-]*[a-zA-Z0-9]@gmail\.com$/.test(email);

  const validateForm = () => {
    // Validate required fields theo backend
    if (!formData.email || !formData.password || !formData.school) {
      toast.error("Vui lòng điền đầy đủ các thông tin bắt buộc");
      return false;
    }

    if (!isValidEmail(formData.email)) {
      toast.error("Email không hợp lệ");
      return false;
    }

    // Validate year of birth (YYYY, 1900..current year)
    const year = String(formData.yearOfBirth || "").trim();
    const currentYear = new Date().getFullYear();
    if (!/^\d{4}$/.test(year) || +year < 1900 || +year > currentYear) {
      toast.error(
        `Năm sinh không hợp lệ (định dạng YYYY, 1900-${currentYear})`
      );
      return false;
    }

    if (!formData.confirmPassword) {
      toast.error("Vui lòng nhập lại mật khẩu");
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Mật khẩu và xác nhận mật khẩu không khớp");
      return false;
    }

    if (formData.password.length < 6) {
      toast.error("Mật khẩu phải có ít nhất 6 ký tự");
      return false;
    }

    // Validate phone number (Vietnamese format) - optional
    if (formData.phoneNumber && !/^[0-9]{10,11}$/.test(formData.phoneNumber)) {
      toast.error("Số điện thoại không hợp lệ (phải có 10-11 số)");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await register({
        fullName: formData.fullName,
        yearOfBirth: formData.yearOfBirth,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        school: formData.school,
        city: formData.city,
        password: formData.password,
      });

      if (response?.success) {
        toast.success(response?.message);
        setFormData({
          fullName: "",
          yearOfBirth: "",
          phoneNumber: "",
          email: "",
          school: "",
          city: "",
          password: "",
          confirmPassword: "",
        });
        navigate("/login");
      } else {
        toast.error(response?.data?.message || "Đăng ký thất bại");
      }
    } catch (error) {
      console.error("Register error:", error);
      toast.error(
        error?.response?.data?.message || "Đăng ký thất bại. Vui lòng thử lại."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] bg-[#f9dfe8]">
      <div className="max-w-[850px] mx-auto px-4 py-12 md:py-16">
        <div className="bg-[#f6d2df] bg-opacity-40 rounded-[8px] shadow-[0_12px_30px_rgba(219,59,109,0.15)] border border-[#efb8c8] p-6 md:p-10">
          {/* Title */}
          <div className="flex justify-center items-center">
            <img src={LogoSvg} alt="PIMAX" className="w-14 h-14" />
            <h1 className="text-[#db3b6d] text-[36px] font-extrabold tracking-wide ml-5">
              ĐĂNG KÍ
            </h1>
          </div>

          {/* Form */}
          <form className="mt-8 mx-auto max-w-[620px]" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Họ và tên (bắt buộc)"
                rightIcon={
                  <UserIcon className="w-5 h-5 text-[#db3b6d] absolute right-3 top-1/2 -translate-y-1/2" />
                }
              />
              <Input
                name="yearOfBirth"
                value={formData.yearOfBirth}
                onChange={handleChange}
                placeholder="Năm sinh (bắt buộc)"
              />

              <Input
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="Số điện thoại (bắt buộc)"
                rightIcon={
                  <PhoneIcon className="w-5 h-5 text-[#db3b6d] absolute right-3 top-1/2 -translate-y-1/2" />
                }
              />
              <Input
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email (bắt buộc)"
                rightIcon={
                  <EnvelopeIcon className="w-5 h-5 text-[#db3b6d] absolute right-3 top-1/2 -translate-y-1/2" />
                }
              />

              <Input
                name="school"
                value={formData.school}
                onChange={handleChange}
                placeholder="Trường (bắt buộc)"
              />
              {/* Tỉnh/Thành phố select */}
              <div className="relative">
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full h-11 md:h-12 rounded-[15px] border border-[#e5a7b7] bg-white/70 pl-4 pr-10 text-[#4b4b4b] focus:outline-none focus:ring-2 focus:ring-[#db3b6d] appearance-none"
                >
                  <option value="">
                    {provincesLoading ? "Đang tải..." : "Chọn tỉnh/thành phố"}
                  </option>
                  {provinces.map((p) => (
                    <option key={p.code} value={p.name}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <Input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Mật khẩu (bắt buộc)"
                rightIcon={
                  <LockClosedIcon className="w-5 h-5 text-[#db3b6d] absolute right-3 top-1/2 -translate-y-1/2" />
                }
              />
              <Input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Nhập lại mật khẩu (bắt buộc)"
                rightIcon={
                  <LockClosedIcon className="w-5 h-5 text-[#db3b6d] absolute right-3 top-1/2 -translate-y-1/2" />
                }
              />
            </div>

            <button
              type="submit"
              className="mt-5 w-full h-12 bg-[#db3b6d] hover:bg-[#c63262] text-white font-semibold rounded-[15px] shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? "ĐANG ĐĂNG KÍ..." : "ĐĂNG KÍ TÀI KHOẢN"}
            </button>

            <div className="text-center mt-5 text-[#4b4b4b]">
              Bạn đã có tài khoản?{" "}
              <Link to="/login" className="text-[#db3b6d] font-semibold">
                ĐĂNG NHẬP NGAY
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
